import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, AfterViewInit, SimpleChange, SimpleChanges, input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { PDFDocument } from 'pdf-lib';
import { PDFFormConfig, KeyValuePair, PDFFieldMapConfig, PDFCheckBoxField, PDFDiffChanges } from '../../interfaces/config';
import { ChildrenOutletContexts, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { match } from 'assert';

@Component({
  selector: 'ppl-editpdf',
  standalone: true,
  imports: [
    NgxExtendedPdfViewerModule,
    CommonModule
  ],
  templateUrl: './editpdf.component.html',
  styleUrl: './editpdf.component.css'
})
export class EditpdfComponent implements OnInit, AfterViewInit {

  @Input() pdfConfig?: PDFFormConfig;
  @Input() pdfFile?: string;
  @Input() readonly?: boolean = false;
  @Input() showSignature?: boolean = true;
  @Input() signature?: string;
  @Input() signatureId?: string;
  @Input() pdfBlob?: Blob;
  @Input() isParticipantFlow?: boolean;
  @Input() isSelf?: boolean;
  @Input() selectedParticipantId?: number;
  @Input() selectedProviderType?: string;
  @Input() isPdfSubmitted?: boolean;
  @Input() highlightedFields?:PDFDiffChanges[]=[];
  @Input() ignorePartBValidationOfI9: boolean = true;
  @Input() resignRequired: boolean = true;
  @Input() isI9Form = false;
  @Input() isW4Form = false;
  @Input() formStatus = '';
  /** Viewer height; e.g. '100%' to fill a host of fixed height. 90vh (80vh on mobile) when not set. */
  @Input() viewerHeight?: string;

  signedField: any;

  @Output() fieldsUpdated = new EventEmitter();
  isLoaded = false;
  customError: string = '';

  form?: FormGroup;
  formKeyMapping: KeyValuePair = {};
  formBusinessMapping: KeyValuePair = {};
  formFieldPrepulatedMapping: KeyValuePair = {};
  formFieldRequiredMapping: KeyValuePair = {};
  showErrors = false;
  errors: any = [];
  checkBoxFields: Record<string, any> = [];
  radioBoxFields: Record<string, any> = [];
  processedFields: string[] = [];
  concernedInputs = ['State', 'line 1', 'line 2', 'line 3', 'line 4', 'line 5', 'empl qualify date', 'service date', 'Date'];
  signField: any;
  signCordinates: any;
  isSignButtonPlaced: boolean = false;
  isDatePlacedForW4: boolean = false;
  isDatePlaced: boolean = false;
  public minZoom = 1;
  public zoom = 100;
  public maxZoom = 1;
  isMobileView = window.innerWidth < 768;
  zoomLevel: number = 100;
  previousFormValues: any = {};
  formKey: any;

  constructor(private fb: FormBuilder, private router: Router) {
  }

  ngOnInit(): void {
    this.isMobileView = window.innerWidth <= 768;
    if (this.isMobileView) {
      this.minZoom = 0.4;
      this.maxZoom = 1.0;
      this.zoom = 40;
      this.zoomLevel = 40;
    }
    const config: KeyValuePair = {};

    if (this.pdfConfig) {
      this.pdfConfig?.fields.forEach((field: PDFFieldMapConfig) => {
        let isFieldRequired = false;

        if (this.isParticipantFlow) {
          isFieldRequired = field.source == 'PTC' ? field.mandatory : false;
        }
        else if (this.isSelf) {
          isFieldRequired = field.mandatory;
        }
        else if (field.source == '' || field.source == this.selectedProviderType) {
          isFieldRequired = field.mandatory;
        }

        if (field.fieldType == "PDCheckBox") {
          this.initCheckBoxes(config, field, isFieldRequired);
        }
        else if (field.fieldType == "PDRadioButton") {
          this.initRadioButtons(config, field, isFieldRequired);
        }
        else {
          if (field.businessName == "todays_date_sec2" && !field.value) {
            field.value = this.getTodayDate();
          }

          const id = field.businessName;
          this.formKeyMapping[field.fieldId] = id;
          this.formBusinessMapping[id] = id;
          this.formFieldPrepulatedMapping[field.fieldId] = field.isPrepopulated;
          this.formFieldRequiredMapping[field.fieldId] = isFieldRequired;
          config[id] = [field.value, isFieldRequired ? Validators.required : null]
        }
      });
    }

    console.log('form initialized');

    this.form = this.fb.group(config);
    this.previousFormValues = this.form.getRawValue();
    this.form.valueChanges.subscribe(fld => {
      this.formKey = null;
      for (const key in fld) {
        if (fld[key] !== this.previousFormValues[key]) {
          if(key === 'Social Security Number') {
            this.formKey = key;
          }
          this.previousFormValues[key] = fld[key];
        }
      }
      if (!this.readonly && this.isPdfSubmitted && !this.isSignButtonPlaced) {
        if (this.resignRequired) {
          this.form?.get(this.signField.businessName)?.setValue(undefined, {
            emitEvent: false
          });
        }

        if (this.resignRequired) {
          this.addSignHereButton(this.signCordinates, this.signField, this.pdfBlob, this.signature);
        }
      }
      this.validateForm();
      this.fieldsUpdated.emit({ data: fld, errors: this.errors, customError: this.customError });
    })
  }


  onZoomChange(newZoom: string | number): void {
    if (typeof newZoom === 'string') {
      this.zoomLevel = parseFloat(newZoom);
    } else {
      this.zoomLevel = newZoom;
    }
    this.updateButtonPosition();
  }

  updateButtonPosition() {
    if (this.signCordinates && this.isMobileView) {
      let factor = this.zoomLevel / 100;
      if (this.zoomLevel > 40 && this.zoomLevel <= 50) {
        factor = factor + 0.05;
      }
      else if (this.zoomLevel > 50 && this.zoomLevel <= 60) {
        factor = factor + 0.09;
      }
      else if (this.zoomLevel > 60 && this.zoomLevel <= 70) {
        factor = factor + 0.12;
      }
      else if (this.zoomLevel > 70 && this.zoomLevel <= 80) {
        factor = factor + 0.15;
      }
      else if (this.zoomLevel > 80 && this.zoomLevel <= 90) {
        factor = factor + 0.19;
      }
      else if (this.zoomLevel > 90 && this.zoomLevel <= 100) {
        factor = factor + 0.23;
      }
      let scaleFactor = 0.12 + factor;

      const button = document.getElementById('CustomClickHereToSignButton');
      if (button) {
        button.style.padding = '7px';
        button.style.width = "100px";
        button.style.fontSize = "11px";
        const buttonXPos = this.signCordinates.xPos * scaleFactor;
        const buttonYPos = this.signCordinates.yPos * scaleFactor;
        button.style.left = `${buttonXPos - 10}px`;
        button.style.bottom = `${buttonYPos - 3}px`;
      }
    }
  }

  initCheckBoxes(config: KeyValuePair, field: PDFFieldMapConfig, isFieldRequired: boolean) {
    if (field.fieldType == "PDCheckBox") {
      field.childFields?.forEach((checkbox: PDFCheckBoxField) => {
        const id = checkbox.fieldId;
        this.formKeyMapping[checkbox.fieldId] = id;
        this.formBusinessMapping[id] = checkbox.fieldId;
        this.formFieldPrepulatedMapping[checkbox.fieldId] = field.isPrepopulated;
        this.formFieldRequiredMapping[checkbox.fieldId] = isFieldRequired;
        config[id] = [checkbox.value, null]
      });

      if (field.mandatory) {
        const checkboxElementId = field.businessName;
        config[checkboxElementId] = ['', null]
        if (!this.checkBoxFields[checkboxElementId]) {
          this.checkBoxFields[checkboxElementId] = [];
        }
        this.checkBoxFields[checkboxElementId].push(field);
        this.formBusinessMapping[checkboxElementId] = checkboxElementId;
      }
    }
  }

  initRadioButtons(config: KeyValuePair, field: PDFFieldMapConfig, isFieldRequired: boolean) {
    if (field.fieldType == "PDRadioButton") {
      field.childFields?.forEach((checkbox: PDFCheckBoxField) => {
        const id = checkbox.fieldId;
        this.formKeyMapping[checkbox.fieldId] = id;
        this.formBusinessMapping[id] = id;
        this.formFieldPrepulatedMapping[checkbox.fieldId] = field.isPrepopulated;
        this.formFieldRequiredMapping[checkbox.fieldId] = isFieldRequired;
        config[id] = [checkbox.value, null]
      });

      if (field.mandatory) {
        const checkboxElementId = field.businessName;
        config[checkboxElementId] = ['', null]
        if (!this.radioBoxFields[checkboxElementId]) {
          this.radioBoxFields[checkboxElementId] = [];
        }
        this.radioBoxFields[checkboxElementId].push(field);
        this.formBusinessMapping[checkboxElementId] = checkboxElementId;
      }
    }
  }

  onFormSubmission() {
    this.isPdfSubmitted = true;
    this.isSignButtonPlaced = false;
  }

  ngAfterViewInit() {
    if (this.pdfConfig) {
      setTimeout(() => {
        this.handleSign();
      }, 1000);
    } else {
      this.isLoaded = true;
    }
  }

  attachFormFieldListeners(event: any) {
    setTimeout(() => {
      const inputFields = document.querySelectorAll(
        '.pdfViewer .annotationLayer input, .pdfViewer .annotationLayer select, .pdfViewer .annotationLayer textarea'
      );
      inputFields.forEach((inputField: any) => {
        inputField.setAttribute('aria-label', this.formKeyMapping[inputField.name]);
        inputField.setAttribute('tabindex', 0)
        if (this.processedFields.indexOf(inputField.name) == -1 || inputField.type == "checkbox" || inputField.type == "radio") {
          this.processedFields.push(inputField.name);
          if (this.signedField && inputField.name == this.signedField.fieldId) {
            inputField.style.display = 'none';
            return;
          }

          const angularFieldName = this.form?.get([this.formKeyMapping[inputField.name]]);
          // if field exsit in form
          if (angularFieldName) {
            // register change event listner (works for all the fields) 
            if (this.formFieldPrepulatedMapping[inputField.name] != 'Y') {
              this.registerEventToInputField(inputField, angularFieldName)
            }

            //ignore for signature textbox
            if (inputField.type == 'text' && inputField.name != 'Signature of Employee') {
              if (this.formFieldPrepulatedMapping[inputField.name] == 'Y') {
                inputField.style.backgroundColor = '#b3e7ff';
              } else if (this.formFieldRequiredMapping[inputField.name]) {
                inputField.style.backgroundColor = '#FFEA00';
              }
            }

            // get the initial value and put it in form
            const angularValue = angularFieldName.value;
            const matchedHighlight = this.highlightedFields?.find(
                    f => f.fieldId === inputField.name
                    );
            if (angularValue) {
              // handle checkbox
              if (inputField.type == "checkbox") {
                const exportValue = inputField.getAttribute('exportvalue');
                inputField.checked = exportValue == angularValue ? true : false;
                if (matchedHighlight) {
                  inputField.style.backgroundColor = ''
                  if(matchedHighlight.op === 'add'){
                      inputField.style.backgroundColor = '#c6f7c6'
                      inputField.disabled = true;
                  }else if(matchedHighlight.op === 'replace'){
                    inputField.style.backgroundColor = '#FFD580'
                  }else{
                    inputField.style.backgroundColor = '#f99';  // 🔴 Highlight changed fields
                  }
                }
              }
              else if (inputField.type == "radio") {
                const exportValue = inputField.getAttribute('exportvalue');
                inputField.checked = exportValue == angularValue ? true : false;
                if (matchedHighlight) {
                  inputField.style.backgroundColor = ''
                  if(matchedHighlight.op === 'add'){
                      inputField.style.backgroundColor = '#c6f7c6'
                      inputField.disabled = true;
                  }else if(matchedHighlight.op === 'replace'){
                    inputField.style.backgroundColor = '#FFD580'
                  }else{
                    inputField.style.backgroundColor = '#f99';  // 🔴 Highlight changed fields
                  }
                }
              } else {
                inputField.value = angularValue;
                if (matchedHighlight) {
                  inputField.style.backgroundColor = ''
                  if(matchedHighlight.op === 'add'){
                    inputField.style.backgroundColor = '#c6f7c6'
                    inputField.disabled = true;
                  }else if(matchedHighlight.op === 'replace'){
                    inputField.style.backgroundColor = '#FFD580'
                  }else{
                    inputField.style.backgroundColor = '#f99';  // 🔴 Highlight changed fields
                  }
                }
              }
              

              //mark field readonly
              if (this.readonly || this.formFieldPrepulatedMapping[inputField.name] == 'Y') {
                if (inputField instanceof HTMLInputElement || inputField instanceof HTMLTextAreaElement) {
                  inputField.readOnly = true;
                } else if (inputField instanceof HTMLSelectElement) {
                  inputField.disabled = true
                }
              }
            }
          }
        }
      });

      this.isLoaded = true;

      const signId = this.signCordinates?.signatureId;
      if (signId) {
        this.form?.get(this.signField.businessName)?.setValue(this.signatureId, {
          emitEvent: false
        });
      }
      else if ((!this.readonly && this.resignRequired) || (this.isI9Form && this.formStatus === 'R')) {
        this.addSignHereButton(this.signCordinates, this.signField, this.pdfBlob, this.signature);
      }

      if (this.isW4Form) {
        this.placeDateforW4Form(false);
      } else {
        this.placeDateforForm(false);
      }

      if (this.pdfConfig?.form_id == '48') {
        this.handlePaymentFormCustomLogic(undefined)
      }

      const canvas = document.querySelectorAll('.page canvas');
      canvas.forEach((canv: any) => {
        var spanelements = canv.querySelectorAll('span[aria-owns]');
        spanelements.forEach((span: HTMLSpanElement) => {
          span.removeAttribute('aria-owns');
        });
      });

      const textLayers = document.querySelectorAll('.textLayer');
      textLayers.forEach((layer: any) => {
        var spanElements = layer.querySelectorAll('span[role=presentation]');
        spanElements.forEach((span: HTMLSpanElement) => {
          const text = span.textContent?.trim();
          if (text?.includes('NYCDPAP')) {
            span.setAttribute('role', 'text');
            span.innerHTML = span.innerHTML.replace(
              /NYCDPAP/g,
              `<span role="text" aria-label="N Y C D P A P"><span aria-hidden="true">NYCDPAP</span></span>`
            );
          }
          if (text?.includes('CDPAP')) {
            span.setAttribute('role', 'text');
            span.innerHTML = span.innerHTML.replace(
              /CDPAP/g,
              `<span role="text" aria-label="C D P A P"><span aria-hidden="true">CDPAP</span></span>`
            );
          }
          if (text?.includes('@ppl')) {
            span.setAttribute('role', 'text');
            span.innerHTML = span.innerHTML.replace(
              /@ppl/g,
              `<span role="text" aria-label="@ p p l "><span aria-hidden="true">@ppl</span></span>`
            );
          }
        });
      });
    }, 1000);
  }

  registerEventToInputField(inputField: any, angularFieldName: any) {
    // config specifically for ny it form
    if (this.pdfConfig?.form_id == "41" && this.concernedInputs.indexOf(inputField.name) != -1) {
      inputField.addEventListener('keyup', () => {
        this.handleEventFromInput(inputField, angularFieldName);
      });
    }

    inputField.addEventListener('input', () => {
      this.handleEventFromInput(inputField, angularFieldName);
      if (this.pdfConfig?.form_id == '48') {
        this.handlePaymentFormCustomLogic(inputField)
      }
    });
  }

  handleEventFromInput(inputField: any, angularFieldName: any) {
    if (inputField.type == "checkbox") {
      const isChecked = inputField.checked;

      if (isChecked) {
        //check if checkbox exist in radio buttons
        Object.entries(this.radioBoxFields).forEach(([key, values]) => {
          if (values.length > 0) {
            const isTheValidFiled = values[0].childFields.filter((e: any) => e.fieldId == inputField.name);
            if (isTheValidFiled && isTheValidFiled.length > 0) {
              values[0].childFields.forEach((radio: any) => {
                if (radio.fieldId !== inputField.name) {
                  var formEle = this.form?.get(radio.fieldId);
                  if (formEle) {
                    formEle.setValue('Off');
                  }
                }
              });
            }
          }
        });
      }

      angularFieldName.markAsTouched();
      const exportValue = inputField.getAttribute('exportvalue');
      angularFieldName.setValue(isChecked ? exportValue : 'Off');
    } else if (inputField.type == "radio") {
      angularFieldName.markAsTouched();
      const exportValue = inputField.getAttribute('exportvalue');
      angularFieldName.setValue(exportValue);
    }
    else {
      angularFieldName.markAsTouched();
      angularFieldName.setValue(inputField.value);
    }
  }

  onPageRendered(event: any) {
    console.log(`Page ${event.pageNumber} rendered`);
    this.attachFormFieldListeners(event);
  }

  navigateToPdfList() {
    this.router.navigate(['/']);
  }

  validateForm() {
    if (this.formKey === 'Social Security Number' && (this.pdfConfig?.form_id === '39' || this.pdfConfig?.form_id === '41')) {
      const ssnControl = this.form?.get('Social Security Number');
      const ssnRaw = ssnControl?.value;
      const ssn = ssnRaw ? String(ssnRaw).trim() : '';
      if (ssn && !/^\d+$/.test(ssn)) {
        this.showErrors = true;
        this.customError = 'Please add your Social Security Number. Enter nine numbers only, no dashes (Example: 123456789).';
        return;
      }
      if (ssn && ssn.length !== 9) {
        this.showErrors = true;
        this.customError = 'Please add your Social Security Number. Enter nine numbers only, no dashes (Example: 123456789).';
        return;
      }
    }
    
    this.calculateErrors();

    this.customError = '';
    Object.entries(this.checkBoxFields).forEach(([key, values]) => {
      let value = '';
      values.forEach((v: any) => {
        v.childFields.forEach((cf: any) => {
          const inputValue = this.form?.get(cf.fieldId)?.value;
          if (value == '' && inputValue != 'Off') {
            value = inputValue;
          }
        });
      });

      if (value == '') {
        this.customError = 'Please select ' + key + ' details';
      }
    });
    this.showErrors = this.customError != '' || !this.form?.valid;

    if (!this.showErrors) {
      this.customError = '';
      Object.entries(this.radioBoxFields).forEach(([key, values]) => {
        let value = '';
        values.forEach((v: any) => {
          v.childFields.forEach((cf: any) => {
            const inputValue = this.form?.get(cf.fieldId)?.value;
            if (value == '' && inputValue != 'Off') {
              value = inputValue;
            }
          });
        });

        if (value == '') {
          this.customError = 'Please select ' + key + ' details';
        }
      });
      this.showErrors = this.customError != '' || !this.form?.valid;
    }

    if (!this.showErrors) {
      this.validateCustomLogic();
    }
  }

  handlePaymentFormCustomLogic(inputField: any) {
    const depositToNewDebitCard = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Deposit to New Debit Card"]`);
    const depositToNewBankAccount = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Direct deposit to a Bank Account or ThirdParty Money App"]`);
    const depositToExistingDebitCredit = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Direct Deposit to An Existing Debit Card"]`);

    let isDepositToNewDebitCardChecked = false;
    let isdepositToNewBankAccountChecked = false;
    let isdepositToExistingDebitCreditChecked = false;
    const emitEvent = inputField ? true : false;

    if (!inputField) {
      if (depositToNewDebitCard.length) {
        const angularFieldName = this.form?.get(['Deposit to New Debit Card']);
        const exportValue = depositToNewDebitCard[0].getAttribute('exportvalue');
        isDepositToNewDebitCardChecked = exportValue == angularFieldName?.value ? true : false;
      }
      if (depositToNewBankAccount.length) {
        const angularFieldName = this.form?.get(['Direct deposit to a Bank Account or ThirdParty Money App']);
        const exportValue = depositToNewBankAccount[0].getAttribute('exportvalue');
        isdepositToNewBankAccountChecked = exportValue == angularFieldName?.value ? true : false;
      }
      if (depositToExistingDebitCredit.length) {
        const angularFieldName = this.form?.get(['Direct Deposit to An Existing Debit Card']);
        const exportValue = depositToExistingDebitCredit[0].getAttribute('exportvalue');
        isdepositToExistingDebitCreditChecked = exportValue == angularFieldName?.value ? true : false;
      }
    } else {
      if (inputField.name == 'Deposit to New Debit Card') {
        isDepositToNewDebitCardChecked = inputField.checked;
      } else if (inputField.name == 'Direct deposit to a Bank Account or ThirdParty Money App') {
        isdepositToNewBankAccountChecked = inputField.checked;
      } else if (inputField.name == 'Direct Deposit to An Existing Debit Card') {
        isdepositToExistingDebitCreditChecked = inputField.checked;
      } else {
        if (inputField.type == "checkbox") {
          this.form?.get(inputField.name)?.setValue(inputField.checked ? 'On' : 'Off');
        } else {
          this.form?.get(inputField.name)?.setValue(inputField.value);
        }
        return;
      }
    }

    if (!isDepositToNewDebitCardChecked && !isdepositToNewBankAccountChecked && !isdepositToExistingDebitCreditChecked) {
      this.turnOffAllPaymentMethodFields(false, false, inputField);
    }
    else if (isdepositToNewBankAccountChecked) {
      if (depositToNewDebitCard[0] instanceof HTMLInputElement) {
        depositToNewDebitCard[0].checked = false;
        this.form?.get('Deposit to New Debit Card')?.setValue('Off', {
          emitEvent: emitEvent
        });
      }

      if (depositToExistingDebitCredit[0] instanceof HTMLInputElement) {
        depositToExistingDebitCredit[0].checked = false;
        this.form?.get('Direct Deposit to An Existing Debit Card')?.setValue('Off', {
          emitEvent: emitEvent
        });
      }
      this.turnOffAllPaymentMethodFields(true, false, inputField);
    } else if (isdepositToExistingDebitCreditChecked) {
      if (depositToNewDebitCard[0] instanceof HTMLInputElement) {
        depositToNewDebitCard[0].checked = false;
        this.form?.get('Deposit to New Debit Card')?.setValue('Off', {
          emitEvent: emitEvent
        });
      }
      if (depositToNewBankAccount[0] instanceof HTMLInputElement) {
        depositToNewBankAccount[0].checked = false;
        this.form?.get('Direct deposit to a Bank Account or ThirdParty Money App')?.setValue('Off', {
          emitEvent: emitEvent
        });
      }
      this.turnOffAllPaymentMethodFields(false, true, inputField);
    } else {
      if (depositToExistingDebitCredit[0] instanceof HTMLInputElement) {
        depositToExistingDebitCredit[0].checked = false;
        this.form?.get('Direct Deposit to An Existing Debit Card')?.setValue('Off', {
          emitEvent: emitEvent
        });
      }
      if (depositToNewBankAccount[0] instanceof HTMLInputElement) {
        depositToNewBankAccount[0].checked = false;
        this.form?.get('Direct deposit to a Bank Account or ThirdParty Money App')?.setValue('Off', {
          emitEvent: emitEvent
        });
      }
      this.turnOffAllPaymentMethodFields(false, false, inputField);
    }
  }

  turnOffAllPaymentMethodFields(enableGroup1: boolean, enableGroup2: boolean, updateChecked: boolean) {
    const checkbox1 = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Bank Checking Account"]`);
    const checkbox2 = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Thirdparty money app"]`);
    const checkbox3 = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Bank Savings Account"]`);
    const textbox1 = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Bank or money app name"]`);
    const textbox2 = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Routing number"]`);
    const textbox3 = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Account number"]`);
    const textbox4 = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Routing number_2"]`);
    const textbox5 = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Account number_2"]`);

    if (checkbox1.length && checkbox1[0] instanceof HTMLInputElement) {
      if (updateChecked) {
        checkbox1[0].checked = false;
        this.form?.get('Bank Checking Account')?.setValue('Off');
      }
      checkbox1[0].disabled = !enableGroup1;
    }
    if (checkbox2.length && checkbox2[0] instanceof HTMLInputElement) {
      if (updateChecked) {
        checkbox2[0].checked = false;
        this.form?.get('Thirdparty money app')?.setValue('Off');
      }
      checkbox2[0].disabled = !enableGroup1;
    }
    if (checkbox3.length && checkbox3[0] instanceof HTMLInputElement) {
      if (updateChecked) {
        checkbox3[0].checked = false;
        this.form?.get('Bank Savings Account')?.setValue('Off');
      }
      checkbox3[0].disabled = !enableGroup1;
    }
    if (textbox1.length && textbox1[0] instanceof HTMLInputElement) {
      if (updateChecked) {
        textbox1[0].value = '';
        this.form?.get('Bank or money app name')?.setValue('');
      }
      textbox1[0].disabled = !enableGroup1;
    }
    if (textbox2.length && textbox2[0] instanceof HTMLInputElement) {
      if (updateChecked) {
        textbox2[0].value = '';
        this.form?.get('Direct Deposit to a Bank account or Third-party app - Routing Number')?.setValue('');
      }
      textbox2[0].disabled = !enableGroup1;
    }
    if (textbox3.length && textbox3[0] instanceof HTMLInputElement) {
      if (updateChecked) {
        textbox3[0].value = '';
        this.form?.get('Direct Deposit to a Bank account or Third-party app - Account Number')?.setValue('');
      }
      textbox3[0].disabled = !enableGroup1;
    }
    if (textbox4.length && textbox4[0] instanceof HTMLInputElement) {
      if (updateChecked) {
        textbox4[0].value = '';
        this.form?.get('Direct Deposit to an existing debit card - Account Number')?.setValue('');
      }
      textbox4[0].disabled = !enableGroup2;
    }
    if (textbox5.length && textbox5[0] instanceof HTMLInputElement) {
      if (updateChecked) {
        textbox5[0].value = '';
        this.form?.get('Direct Deposit to an existing debit card - Routing Number')?.setValue('');
      }
      textbox5[0].disabled = !enableGroup2;
    }
  }

  validatePaymentForm() {
    const depositToNewBankAccount = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Direct deposit to a Bank Account or ThirdParty Money App"]`);
    const depositToExistingDebitCredit = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Direct Deposit to An Existing Debit Card"]`);

    let isdepositToNewBankAccountChecked = false;
    let isdepositToExistingDebitCreditChecked = false;

    if (depositToNewBankAccount.length) {
      const angularFieldName = this.form?.get(['Direct deposit to a Bank Account or ThirdParty Money App']);
      const exportValue = depositToNewBankAccount[0].getAttribute('exportvalue');
      isdepositToNewBankAccountChecked = exportValue == angularFieldName?.value ? true : false;
    }
    if (depositToExistingDebitCredit.length) {
      const angularFieldName = this.form?.get(['Direct Deposit to An Existing Debit Card']);
      const exportValue = depositToExistingDebitCredit[0].getAttribute('exportvalue');
      isdepositToExistingDebitCreditChecked = exportValue == angularFieldName?.value ? true : false;
    }

    if (isdepositToExistingDebitCreditChecked) {
      const textbox4 = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Routing number_2"]`);
      const textbox5 = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Account number_2"]`);

      if (textbox4.length && textbox4[0] instanceof HTMLInputElement) {
        const routingNumber = textbox4[0].value.trim();

        if (routingNumber === '') {
          return {
            valid: false,
            error: 'Routing number is required for direct deposit to an existing debit card.'
          };
        }

        const validationResult = this.validateRoutingNumber(routingNumber);
        if (!validationResult.valid) {
          return {
            valid: false,
            error: validationResult.error ?? ''
          };
        }
      }

      if (textbox5.length && textbox5[0] instanceof HTMLInputElement) {
        const accountNumber = textbox5[0].value.trim();

        // Check if empty
        if (accountNumber === '') {
          return {
            valid: false,
            error: 'Account number is required for direct deposit to an existing debit card.'
          };
        }

        // Validate numeric and length between 6 and 17
        const accountRegex = /^\d{6,17}$/;
        if (!accountRegex.test(accountNumber)) {
          return {
            valid: false,
            error: 'Account number must be numeric and between 6 and 17 digits.'
          };
        }
      }
    }

    if (isdepositToNewBankAccountChecked) {
      const checkbox1 = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Bank Checking Account"]`);
      const checkbox2 = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Thirdparty money app"]`);
      const checkbox3 = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Bank Savings Account"]`);
      const textbox1 = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Bank or money app name"]`);
      const textbox2 = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Routing number"]`);
      const textbox3 = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="Account number"]`);

      let isCheckBox1Checked = false;
      let isCheckBox2Checked = false;
      let isCheckBox3Checked = false;
      if (checkbox1.length && checkbox1[0] instanceof HTMLInputElement) {
        isCheckBox1Checked = checkbox1[0].checked;
      }
      if (checkbox2.length && checkbox2[0] instanceof HTMLInputElement) {
        isCheckBox2Checked = checkbox2[0].checked;
      }
      if (checkbox3.length && checkbox3[0] instanceof HTMLInputElement) {
        isCheckBox3Checked = checkbox3[0].checked;
      }

      const checkedCount = [isCheckBox1Checked, isCheckBox2Checked, isCheckBox3Checked].filter(Boolean).length;

      if (checkedCount != 1) {
        return { valid: false, error: 'Select one account type for direct deposit to a bank account or third-party money app.' };
      }
      if (textbox1.length && textbox1[0] instanceof HTMLInputElement && textbox1[0].value.trim() == '') {
        return { valid: false, error: 'Bank or money app name is required for direct deposit to a bank account or third-party money app.' };
      }
      if (textbox2.length && textbox2[0] instanceof HTMLInputElement) {
        const routingNumber = textbox2[0].value.trim();

        if (routingNumber === '') {
          return {
            valid: false,
            error: 'Routing number is required for direct deposit to a bank account or third-party money app.'
          };
        }

        const validationResult = this.validateRoutingNumber(routingNumber);
        if (!validationResult.valid) {
          return {
            valid: false,
            error: validationResult.error ?? ''
          };
        }
      }
      if (textbox3.length && textbox3[0] instanceof HTMLInputElement) {
        const accountNumber = textbox3[0].value.trim();

        // Check if empty
        if (accountNumber === '') {
          return {
            valid: false,
            error: 'Account number is required for direct deposit to a bank account or third-party money app.'
          };
        }

        // Validate numeric and length between 6 and 17
        const accountRegex = /^\d{6,17}$/;
        if (!accountRegex.test(accountNumber)) {
          return {
            valid: false,
            error: 'Account number must be numeric and between 6 and 17 digits.'
          };
        }
      }
    }

    return { valid: true, error: '' };
  }

  validateCustomLogic() {
    if (this.pdfConfig?.form_id == '44') {

      if (!this.ignorePartBValidationOfI9) {
        // rules for PART A or PART B & C 
        let isPartAInValid = !this.form?.value['List A - Document Issuing Authority 1'] || !this.form?.value['List A - Document Title 1'];
        let isPartBCInValid = !this.form?.value['List B - Document Title'] ||
          !this.form?.value['List B - Document Issuing Authority'] ||
          !this.form?.value['List C - Document Title'] || !this.form?.value['List C - Document Issuing Authority'];

        //let isPartBCTouched = this.form?.get('lb_doc_title')?.touched || this.form?.get('List B - Document Issuing Authority')?.touched ||
        //  this.form?.get('List C - Document Title')?.touched || this.form?.get('List C - Document Issuing Authority')?.touched;

        if (isPartAInValid && isPartBCInValid) {
          this.showErrors = true;
          this.customError = "Document Title 1 and Issuing Authority are required";
        } else {

          let isLastNameFirstName = !this.form?.value['last_name_auth_rep'];
          let signOfAuthIsInvalid = !this.form?.value['signature_auth_rep'];
          let orgNameIsInvalid = !this.form?.value['auth_rep_business_name'];
          let orgAddressIsInvalid = !this.form?.value['auth_rep_address'];
          let todayDateIsInvalid = !this.form?.value['todays_date_sec2'];
          let dateOfEmploymentIsInvalid = !this.form?.value['First Day of Employment'];

          if (isLastNameFirstName) {
            this.showErrors = true;
            this.customError = "Last Name First Name and Title of Employer or Authorized Representative is required";
          } else if (signOfAuthIsInvalid) {
            this.showErrors = true;
            this.customError = "Signature of Employer or AR is required";
          } else if (orgNameIsInvalid) {
            this.showErrors = true;
            this.customError = "Employers Business or Org Name is required";
          }
          else if (orgAddressIsInvalid) {
            this.showErrors = true;
            this.customError = "Employers Business or Org Address is required";
          } else if (todayDateIsInvalid) {
            this.showErrors = true;
            this.customError = "Today's Date is required";
          }
          else if (dateOfEmploymentIsInvalid) {
            this.showErrors = true;
            this.customError = "First Day of Employment is required";
          }

          if (!todayDateIsInvalid && this.isInvalidDate(this.form?.value['todays_date_sec2'])) {
            this.showErrors = true;
            this.customError = "Today's date has an invalid value, or is not in the mm/dd/yyyy format.";
          }

          if (!dateOfEmploymentIsInvalid && this.isInvalidDate(this.form?.value['First Day of Employment'])) {
            this.showErrors = true;
            this.customError = "First Day of Employment has an invalid value, or is not in the mm/dd/yyyy format.";
          }
        }
      }

      // rules for immigration or citizen status

      if (this.form?.value['CB_3'] !== 'Off') {
        // uscis number is required
        if (!this.form?.value['USCIS A-Number 3(a)']) {
          this.showErrors = true;
          this.customError = "USCIS or A Number is required";
        }
      } else if (this.form?.value['CB_4'] !== 'Off') {
        //one of these values are required
        let hasEnteredAnyInfo = this.form?.value['USCIS A-Number'] || this.form?.value['Form I94 Admission Number'] || this.form?.value['Foreign Passport Number'];
        if (!hasEnteredAnyInfo) {
          this.showErrors = true;
          this.customError = "One of the USCIS - A Number, Form I-94 Admission Number, Foreign Passport Number are required";
        }
      }
    }

    if (this.pdfConfig?.form_id == '48') {
      let count = 0;
      Object.entries(this.checkBoxFields).forEach(([key, values]) => {
        values.forEach((v: any) => {
          v.childFields.forEach((cf: any) => {
            const inputValue = this.form?.get(cf.fieldId)?.value;
            if (inputValue == 'On') {
              count++
            }
          });
        });

        if (count > 1) {
          this.customError = 'Choose only one payment method to proceed.';
        }
      });

      const status = this.validatePaymentForm();
      if (!status.valid) {
        this.customError = status.error;
      }
    }

    if (this.pdfConfig?.form_id === '39' || this.pdfConfig?.form_id === '41') {
     this.validateSSN();
    }
  }

  validateSSN() {
    console.log("validating ssn")
   const ssnControl = this.form?.get('Social Security Number');
      const ssnRaw = ssnControl?.value;
      const ssn = ssnRaw ? String(ssnRaw).trim() : '';
      if (ssn && !/^\d+$/.test(ssn)) {
        this.showErrors = true;
        this.customError = 'Please add your Social Security Number. Enter nine numbers only, no dashes (Example: 123456789).';
        return;
      }
      if (ssn && ssn.length !== 9) {
        this.showErrors = true;
        this.customError = 'Please add your Social Security Number. Enter nine numbers only, no dashes (Example: 123456789).';
        return;
      }
  }

  calculateErrors() {
    this.errors = [];
    Object.keys(this.form?.value).forEach(field => {
      const control = this.form?.get(field);
      if (control) {
        const controlErrors = control.errors;
        if (controlErrors !== null) {
          Object.keys(controlErrors).forEach(keyError => {
            this.errors.push({
              controlName: field,
              errorName: keyError,
              errorValue: controlErrors[keyError],
              businessName: this.formBusinessMapping[field]
            });
          });
        }
      }
    });

    // This removes duplicates
    this.errors = this.errors.filter((error: any, index: any, self: any) => self.findIndex((t: any) => {
      return t.controlName === error.controlName && t.errorName === error.errorName;
    }) === index);
    if (this.errors.length > 0) {
      this.showErrors = true;
    }
  }

  async handleSign() {
    if (!this.showSignature || !this.signature) {
      this.isLoaded = true;
      return;
    }

    if (this.pdfBlob && this.pdfConfig && this.showSignature) {
      let coordinates: any;
      let field: any;
      if (this.isParticipantFlow) {
        const requiredFields = this.pdfConfig.fields.filter(e => e.source == "PTC" && e.fieldType == "CustomSignature");
        if (requiredFields.length > 0) {
          field = requiredFields[0];
          coordinates = field.coordinates;
        }
      }
      else if (!this.isParticipantFlow && !this.selectedProviderType) {
        const requiredFields = this.pdfConfig.fields.filter(e => e.fieldType == "CustomSignature");
        if (requiredFields.length > 0) {
          field = requiredFields[0];
          coordinates = field.coordinates;
        }
      }
      else {
        const requiredFields = this.pdfConfig.fields.filter(e => e.source == this.selectedProviderType && e.fieldType == "CustomSignature");
        if (requiredFields.length > 0) {
          // const fieldWithCoordinate = requiredFields.filter(e => e.coordinates != null);
          field = requiredFields[0];
          coordinates = field.coordinates;
        }
      }

      if (!this.readonly) {
        if (coordinates && this.pdfBlob) {
          this.signCordinates = coordinates;
          this.signField = field;
          // Add the signature to the PDF
          await this.addImageToPdf(
            this.pdfBlob,
            this.signature,
            coordinates.pageId,
            coordinates.xPos,
            coordinates.yPos,
            coordinates.signatureWidth,
            coordinates.signatureHeight
          );

          if (!this.resignRequired) {
            this.form?.get(field.businessName)?.setValue(this.signatureId);
            this.signedField = field;
          }

        } else {
          console.log("Coordinates couldn't be found for signature");
        }
      }
      else {
        //Read-only views (e.g. form history) show the signature only when the record was signed:
        //a saved signature id, or a completed form
        const isSigned = !!coordinates?.signatureId || !!this.isPdfSubmitted;
        if (coordinates && !isSigned) {
          this.isLoaded = true;
        } else if (coordinates) {
          await this.addImageToPdf(
            this.pdfBlob,
            this.signature,
            coordinates.pageId,           // Page number where the image will be placed
            coordinates.xPos,         // X position (from the bottom-left of the page)
            coordinates.yPos,         // Y position (from the bottom-left of the page)
            coordinates.signatureWidth,         // Width of the image
            coordinates.signatureHeight           // Height of the image
          );

          this.form?.get(field.businessName)?.setValue(this.signatureId);
          this.signedField = field;
        } else {
          console.log("coordinates couldn't found to sign");
        }
      }
    }
  }

  addSignHereButton(coordinates: any, field: any, pdfFile: any, signature: any) {
    if (this.isSignButtonPlaced) {
      return;
    }

    if (!signature) {
      this.errors.push({
        controlName: field,
        errorName: 'Signature not found, Please add your signature in Signature Tab',
        errorValue: 'Signature not found, Please add your signature in Signature Tab',
        businessName: this.formBusinessMapping[field]
      });

      return;
    }

    const annotationLayer = document.querySelector(`.pdfViewer .page[data-page-number="${coordinates.pageId}"] .annotationLayer`);

    if (!annotationLayer) {
      console.log('annotation layer in specific page not found')
      return;
    }
    const existingButton = document.getElementById('CustomClickHereToSignButton');
    if (!existingButton) {
      const button = document.createElement('button');
      let scaleFactor = 0;
      if (this.isMobileView) {
        scaleFactor = 0.52;
        button.style.padding = '7px';
        button.style.width = '100px';
        button.style.fontSize = 'xx-small';
      } else {
        scaleFactor = 1.34;
        button.style.padding = '13px';
        button.style.width = '140px';
      }

      const buttonXPos = coordinates.xPos * scaleFactor;
      const buttonYPos = coordinates.yPos * scaleFactor;

      button.innerText = "Click Here to Sign";
      button.id = "CustomClickHereToSignButton";
      button.style.position = 'absolute';
      button.tabIndex = 0;
      button.ariaLabel = 'Click Here to Sign';
      button.style.left = `${buttonXPos - 10}px`;
      button.style.bottom = `${buttonYPos - 3}px`;
      button.style.zIndex = '2147483647'; // Ensure the button is above the PDF content
      button.style.background = 'repeating-linear-gradient(45deg, yellow, #f4f4ee 10px)';
      button.style.borderRadius = '5px';
      button.style.cursor = 'pointer';
      button.style.pointerEvents = 'auto';
      button.style.color = 'red';
      button.style.fontWeight = '700';
      button.style.border = '2px solid red';

      // Add click event to the button to trigger the signature action
      button.addEventListener('click', () => {
        // Hide the button once clicked
        button.style.display = 'none';

        // Set the form value for the signature field
        this.form?.get(field.businessName)?.setValue(this.signatureId);
        this.signedField = field;

         if (this.isW4Form) {
           this.placeDateforW4Form(true);
         } else {
            this.placeDateforForm(true);
         }

      });

      if (!this.isSignButtonPlaced) {
        // Append the button to the annotation layer
        annotationLayer?.appendChild(button);
        this.isSignButtonPlaced = true;
        this.updateButtonPosition();
      }
    } else {
      existingButton.style.display = 'block';
      this.isSignButtonPlaced = true;
      this.updateButtonPosition();
    }
  }

  placeDateforW4Form(forceDateForTodayDate: boolean) {
    if (this.isDatePlacedForW4 && !forceDateForTodayDate) return;

    if (this.pdfConfig) {
      let coordinates: any;
      let field: any;
      const requiredFields = this.pdfConfig.fields.filter(e => e.businessName == "Signature Date");
      if (requiredFields.length > 0) {
        field = requiredFields[0];
        coordinates = field.coordinates;
      }

      const annotationLayer = document.querySelector(`.pdfViewer .page[data-page-number="${coordinates?.pageId}"] .annotationLayer`);

      if (!annotationLayer) {
        console.log('annotation layer in specific page not found')
        return;
      }

      const existingDateField = document.getElementById('CustomW4DateField');
      // remove existing date field if any
      if (existingDateField) {
        existingDateField.remove();
      }

      const dateDiv = document.createElement('div');
      let placementXPos = 0;
      let scaleFactor = 0;
      if (this.isMobileView) {
        scaleFactor = 0.52;
        dateDiv.style.fontSize = '8px';
        placementXPos = 40;
      } else {
        scaleFactor = 1.34;
        dateDiv.style.fontSize = '12px';
        placementXPos = 80;
      }

      const dateXPos = coordinates.xPos * scaleFactor;
      const dateYPos = coordinates.yPos * scaleFactor;
      const date = forceDateForTodayDate ? this.getTodayDate() : (field.value ? field.value : this.getTodayDate());
      dateDiv.id = 'CustomW4DateField';
      dateDiv.style.position = 'absolute';
      dateDiv.tabIndex = 0;
      dateDiv.ariaLabel = 'Signature Date';
      dateDiv.style.left = `${dateXPos + placementXPos}px`;
      dateDiv.style.bottom = `${dateYPos}px`;
      dateDiv.style.zIndex = '2147483647';
      dateDiv.style.color = 'black';
      dateDiv.style.fontWeight = '500';
      dateDiv.innerText = date;
      // Append the date div to the annotation layer
      annotationLayer?.appendChild(dateDiv);
      this.form?.get(field.businessName)?.setValue(date, {
        emitEvent: false
      });
      this.isDatePlacedForW4 = true;
    }
  }

  placeDateforForm(forceDateForTodayDate: boolean) {
    if (this.isDatePlaced && !forceDateForTodayDate) return;

    if (this.pdfConfig) {
      let field: any;
      const requiredFields = this.pdfConfig.fields.filter(e => e.businessName.includes("Signature Date"));
      if (requiredFields.length > 0) {
        field = requiredFields[0];
      }

      if(forceDateForTodayDate) {
        const date = this.getTodayDate();
        this.setFormFieldValue('Personal Assistant Signature Date', date);
        console.log(this.form?.get('Personal Assistant Signature Date')?.value);
        this.isDatePlaced = true;
      }

    }
  }

  async addImageToPdf(pdfFile: Blob, sign: string, pageNumber: number, xPos: number, yPos: number, width: number, height: number) {
    try {
      const pdfBytes = await pdfFile.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
      const base64 = sign.split(',')[1];
      const signBytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      const image = await pdfDoc.embedPng(signBytes);
      const pages = pdfDoc.getPages();
      const page = pages[pageNumber - 1];
      if (page) {
        page.drawImage(image, {
          x: xPos,
          y: yPos + 5,
          width: width,
          height: height,
        });
        const modifiedPdfBytes = await pdfDoc.save();
        const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });
        this.pdfFile = URL.createObjectURL(blob);
      }
    }
    catch (error) {
      console.error('error signing the PDF', error)
    }
    this.isLoaded = true;
  }

  isInvalidDate(dateString: string): boolean {
    // Check if the format is mm/dd/yyyy using regex
    const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;

    // First check if the string is non-empty and matches the format
    if (!dateString || !regex.test(dateString)) {
      return true;
    }

    // Parse the date string
    const [month, day, year] = dateString.split('/').map(Number);

    // Check if the date is valid
    const date = new Date(year, month - 1, day);

    // Ensure the parsed date matches the original input (handles invalid dates like 02/30/2025)
    const isValid = date.getMonth() === month - 1 && date.getDate() === day;
    return !isValid;
  }

  getTodayDate(): string {
    const today = new Date();
    const month = today.getMonth() + 1; // getMonth() returns a zero-based index (0-11)
    const day = today.getDate();
    const year = today.getFullYear();

    // Format the date as mm/dd/yyyy
    return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
  }

  setFormFieldValue(businessName: string, value: string) {
    this.form?.get(businessName)?.setValue(value);
    const selector = `.pdfViewer .annotationLayer input[aria-label="${businessName}"], .pdfViewer .annotationLayer textarea[aria-label="${businessName}"]`;
    const inputEl = document.querySelector(selector) as HTMLInputElement | HTMLTextAreaElement | null;
    if (inputEl) {
      inputEl.value = value;
    }
  }

  validateRoutingNumber(routingNumber: string): { valid: boolean; error?: string } {
    // Check if it's exactly 9 digits and numeric
    const regex = /^\d{9}$/;
    if (!regex.test(routingNumber)) {
      return {
        valid: false,
        error: "Routing number must be exactly 9 numeric digits.",
      };
    }

    const digits = routingNumber.split('').map(Number);

    // ABA Checksum calculation
    const checksum =
      (digits[0] + digits[3] + digits[6]) * 3 +
      (digits[1] + digits[4] + digits[7]) * 7 +
      (digits[2] + digits[5] + digits[8]) * 1;

    if (checksum % 10 !== 0) {
      return {
        valid: false,
        error: "Invalid routing number: ABA checksum validation failed.",
      };
    }

    return { valid: true };
  }
}